import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  Clock,
  Inbox,
  Play,
  TrendingUp,
} from 'lucide-react';
import { ticketDisplaySubject } from '@t3/shared';
import type {
  ActiveTimer,
  AgentDashboardResponse,
  Project,
  TicketSummary,
} from '@t3/shared';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Card, StatusBadge, TypeBadge } from '../components/Card.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

interface CompTimeBalance {
  balanceHours: number;
  capHours: number;
  totalAccruedHours: number;
  totalUsedHours: number;
}

interface TimesheetEntry {
  id: string;
  startedAt: string;
  durationMinutes: number;
  voidedAt: string | null;
  ticketDisplayNumber: number;
  ticketSubject: string;
  organizationName: string;
  billingBucket: string;
}

function startOfTodayLocal(): Date {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function endOfTodayLocal(): Date {
  const d = startOfTodayLocal();
  d.setDate(d.getDate() + 1);
  return d;
}

function formatElapsed(startedAt: string): string {
  const ms = Date.now() - new Date(startedAt).getTime();
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
}

// Self-contained ticking clock so the dashboard doesn't re-render every
// second while a timer runs — a page-wide re-render rewrites list/summary
// DOM and wipes any text the user is trying to select/copy.
function LiveElapsed({ startedAt }: { startedAt: string }): React.ReactElement {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  return <>{formatElapsed(startedAt)}</>;
}

export function DashboardPage(): React.ReactElement {
  useDocumentTitle('Dashboard');
  const { agent, remainingBackupCodes } = useAuth();
  const [stats, setStats] = useState<AgentDashboardResponse | null>(null);
  const [myTickets, setMyTickets] = useState<TicketSummary[] | null>(null);
  const [atRisk, setAtRisk] = useState<Project[] | null>(null);
  const [comp, setComp] = useState<CompTimeBalance | null>(null);
  const [todaysEntries, setTodaysEntries] = useState<TimesheetEntry[] | null>(null);
  const [activeTimer, setActiveTimer] = useState<ActiveTimer | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!agent) return;
    const from = startOfTodayLocal().toISOString();
    const to = endOfTodayLocal().toISOString();
    void Promise.all([
      apiFetch<AgentDashboardResponse>('/api/dashboard/agent').then(setStats).catch(() => undefined),
      apiFetch<{ tickets: TicketSummary[] }>(
        `/api/tickets?assignedAgentId=${agent.id}&closed=false`,
      )
        .then((r) => setMyTickets(r.tickets.slice(0, 8)))
        .catch(() => setMyTickets([])),
      apiFetch<{ projects: Project[] }>(`/api/projects?atRisk=true&assignedAgentId=${agent.id}`)
        .then((r) => setAtRisk(r.projects))
        .catch(() => setAtRisk([])),
      apiFetch<{ balance: CompTimeBalance }>('/api/timesheets/comp-time')
        .then((r) => setComp(r.balance))
        .catch(() => undefined),
      apiFetch<{ entries: TimesheetEntry[] }>(
        `/api/timesheets?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      )
        .then((r) => setTodaysEntries(r.entries))
        .catch(() => setTodaysEntries([])),
      apiFetch<{ timer: ActiveTimer | null }>('/api/timer')
        .then((r) => setActiveTimer(r.timer))
        .catch(() => undefined),
    ]).catch((err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Dashboard had trouble loading.');
    });
  }, [agent]);

  if (!agent) return <></>;

  const todaysMinutes = (todaysEntries ?? [])
    .filter((e) => !e.voidedAt)
    .reduce((s, e) => s + e.durationMinutes, 0);
  const billableToday = (todaysEntries ?? [])
    .filter((e) => !e.voidedAt && e.billingBucket !== 'non_billable')
    .reduce((s, e) => s + e.durationMinutes, 0);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[var(--color-header)]">Welcome, {agent.name.split(' ')[0]}</h1>
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
        </div>
        {activeTimer && (
          <Link
            to={`/tickets/${activeTimer.ticketId}`}
            className="flex items-center gap-3 rounded-[var(--radius-card)] border border-[var(--color-t3-blue)] bg-[var(--color-t3-blue)]/10 px-4 py-2 text-sm hover:bg-[var(--color-t3-blue)]/20"
          >
            <Play className="h-4 w-4 text-[var(--color-t3-blue)]" />
            <div>
              <div className="text-xs uppercase tracking-wide text-[var(--color-t3-blue)]">Timer running</div>
              <div className="font-mono text-lg font-bold text-[var(--color-header)]">
                <LiveElapsed startedAt={activeTimer.startedAt} />
              </div>
            </div>
            <div className="ml-2 max-w-[180px] truncate text-xs text-[var(--color-muted)]">
              T-{activeTimer.ticketDisplayNumber} · {activeTimer.ticketSubject}
            </div>
          </Link>
        )}
      </header>

      {error && (
        <Card className="border-[var(--color-danger)]/30 bg-red-50/40">
          <p className="text-sm text-[var(--color-danger)]">{error}</p>
        </Card>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          icon={<Inbox className="h-5 w-5" />}
          label="Assigned to me"
          value={stats?.assignedToMe}
          link="/tickets?assignedAgentId=me&closed=false"
        />
        <Stat
          icon={<TrendingUp className="h-5 w-5" />}
          label="Open across all"
          value={stats?.open}
          link="/tickets?closed=false"
        />
        <Stat
          icon={<Clock className="h-5 w-5" />}
          label="Today"
          value={`${(todaysMinutes / 60).toFixed(2)}h`}
          sub={`${(billableToday / 60).toFixed(2)}h billable`}
          link="/timesheets"
        />
        <Stat
          icon={<CheckCircle2 className="h-5 w-5" />}
          label="Comp time"
          value={comp ? `${comp.balanceHours.toFixed(2)}h` : '—'}
          sub={comp ? `of ${comp.capHours}h cap` : undefined}
          link="/timesheets"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <SectionHeader to="/tickets?assignedAgentId=me&closed=false" label="My open tickets" />
          {myTickets === null ? (
            <p className="text-[var(--color-muted)]">Loading…</p>
          ) : myTickets.length === 0 ? (
            <Card>
              <p className="text-center text-sm text-[var(--color-muted)]">
                Nothing assigned. Pick something from the queue or take a breath.
              </p>
            </Card>
          ) : (
            <div className="overflow-hidden rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white">
              <table className="w-full text-sm">
                <tbody>
                  {myTickets.map((t) => (
                    <tr key={t.id} className="border-t border-[var(--color-border)] hover:bg-[var(--color-surface)]/40">
                      <td className="px-3 py-2 font-mono text-xs text-[var(--color-muted)]">T-{t.displayNumber}</td>
                      <td className="px-3 py-2"><TypeBadge type={t.type} /></td>
                      <td className="px-3 py-2">
                        <Link to={`/tickets/${t.id}`} className="font-medium text-[var(--color-t3-blue)] hover:underline">
                          {ticketDisplaySubject(t.clientTicketNumber, t.subject)}
                        </Link>
                        <div className="text-xs text-[var(--color-muted)]">{t.organizationName}</div>
                      </td>
                      <td className="px-3 py-2"><StatusBadge status={t.status}>{t.status.replace(/_/g, ' ')}</StatusBadge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <SectionHeader to="/projects?atRisk=true" label="At-risk projects" />
          {atRisk === null ? (
            <p className="text-[var(--color-muted)]">Loading…</p>
          ) : atRisk.length === 0 ? (
            <Card>
              <p className="text-center text-sm text-[var(--color-muted)]">Nothing at risk. 🎉</p>
            </Card>
          ) : (
            <Card>
              <ul className="space-y-2 text-sm">
                {atRisk.slice(0, 6).map((p) => {
                  const usedH = p.consumedMinutes / 60;
                  const pct = p.budgetedHours > 0 ? Math.min(100, (usedH / p.budgetedHours) * 100) : 0;
                  return (
                    <li key={p.id} className="flex items-start gap-2">
                      <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--color-danger)]" />
                      <div className="min-w-0 flex-1">
                        <Link
                          to={`/projects/${p.id}`}
                          className="block truncate font-medium text-[var(--color-t3-blue)] hover:underline"
                        >
                          {p.name}
                        </Link>
                        <div className="text-xs text-[var(--color-muted)]">
                          {p.organizationName} · {pct.toFixed(0)}% consumed
                          {p.dueDate ? ` · due ${p.dueDate}` : ''}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Card>
          )}

          <SectionHeader label="By type" />
          <Card>
            <ul className="space-y-1 text-sm">
              {(['escalation', 'project', 'quote', 'chat'] as const).map((t) => (
                <li key={t} className="flex items-center justify-between">
                  <Link to={`/tickets?type=${t}`} className="capitalize text-[var(--color-t3-blue)] hover:underline">
                    {t}
                  </Link>
                  <span className="font-mono text-[var(--color-muted)]">{stats?.byType[t] ?? '—'}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>

      {remainingBackupCodes !== null && remainingBackupCodes <= 3 && (
        <Card className="border-[var(--color-danger)]/30 bg-red-50/40">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 text-[var(--color-danger)]" />
            <div className="text-sm text-[var(--color-danger)]">
              Only {remainingBackupCodes} backup code{remainingBackupCodes === 1 ? '' : 's'} left.
              Re-enroll TOTP from your account settings to generate a new set.
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

function SectionHeader({ to, label }: { to?: string; label: string }): React.ReactElement {
  return (
    <div className="flex items-baseline justify-between">
      <h2 className="text-base font-semibold text-[var(--color-header)]">{label}</h2>
      {to && (
        <Link to={to} className="text-xs text-[var(--color-t3-blue)] hover:underline">
          View all →
        </Link>
      )}
    </div>
  );
}

function Stat({
  icon,
  label,
  value,
  sub,
  link,
}: {
  icon?: React.ReactNode;
  label: string;
  value: number | string | undefined;
  sub?: string;
  link?: string;
}): React.ReactElement {
  const content = (
    <Card className="h-full transition hover:border-[var(--color-t3-blue)]">
      <div className="flex items-start justify-between">
        <div className="text-sm font-medium text-[var(--color-muted)]">{label}</div>
        {icon && <div className="text-[var(--color-t3-blue)]">{icon}</div>}
      </div>
      <div className="mt-2 text-3xl font-bold text-[var(--color-header)]">
        {value === undefined ? '…' : value}
      </div>
      {sub && <div className="mt-0.5 text-xs text-[var(--color-muted)]">{sub}</div>}
    </Card>
  );
  return link ? <Link to={link}>{content}</Link> : content;
}

void CalendarClock; // kept import for future "next due / cutover" stat
